import { useCallback, useMemo, useState } from 'react';
import {
  U21Table,
  U21Button,
  U21TextField,
  U21Select,
  U21Section,
  U21Typography,
  U21TableBodyCellProps,
  U21Spacer,
  U21SelectOptionProps,
} from 'app/shared/u21-ui/components';
import type { U21TableColumn } from 'app/shared/u21-ui/components';
import { AIAgentConfiguration } from 'app/modules/aiAdmin/models';
import { useGetAiAgents } from 'app/modules/aiAdmin/queries/useGetAiAgents';
import { useAutoSetupAiAgent } from 'app/modules/aiAdmin/queries/useAutoSetupAiAgent';
import { toSentenceCase } from 'app/shared/utils/string';
import styled from 'styled-components';

const STATUS_OPTIONS: U21SelectOptionProps<AIAgentConfiguration['status']>[] = [
  { text: 'Unconfigured', value: 'unconfigured' },
  { text: 'Needs permissions', value: 'needs_permissions' },
  { text: 'Configured', value: 'configured' },
];

export const AIAgentList = () => {
  const [orgNameFilter, setOrgNameFilter] = useState('');
  const [statusFilter, setStatusFilter] =
    useState<AIAgentConfiguration['status']>();
  const [pendingOrgId, setPendingOrgId] = useState<number | null>(null);

  // Use React Query hook to fetch AI agents
  const {
    data: agents = [],
    isLoading,
    isRefetching,
    refetch,
  } = useGetAiAgents();

  // Setup agent mutation
  const { mutateAsync: setupAgent, isPending: isSetupLoading } =
    useAutoSetupAiAgent();

  const handleAction = useCallback(
    async (orgId: number) => {
      setPendingOrgId(orgId);
      try {
        await setupAgent({ org_id: orgId });
      } catch {
      } finally {
        setPendingOrgId(null);
      }
    },
    [setupAgent],
  );

  const getActionButton = useCallback(
    (row: AIAgentConfiguration) => {
      const isActionLoading = isSetupLoading && pendingOrgId === row.org_id;

      switch (row.status) {
        case 'unconfigured':
          return (
            <U21Button
              variant="contained"
              onClick={() => handleAction(row.org_id)}
              loading={isActionLoading}
              disabled={isSetupLoading}
            >
              Configure Agent
            </U21Button>
          );
        case 'needs_permissions':
          return (
            <U21Button
              variant="outlined"
              onClick={() => handleAction(row.org_id)}
              loading={isActionLoading}
              disabled={isSetupLoading}
            >
              Update Permissions
            </U21Button>
          );
        case 'configured':
          return (
            <U21Typography color="text.secondary" variant="body2">
              All set
            </U21Typography>
          );
        default:
          return null;
      }
    },
    [isSetupLoading, pendingOrgId, handleAction],
  );

  const columns: U21TableColumn<AIAgentConfiguration, any>[] = useMemo(
    () => [
      {
        id: 'org_name',
        Header: 'Organization',
        accessor: 'org_name',
      },
      {
        id: 'status',
        Header: 'Status',
        accessor: (row) => toSentenceCase(row.status.replaceAll('_', ' ')),
      },
      {
        id: 'action',
        Header: 'Action',
        accessor: 'status',
        Cell: ({ row }: U21TableBodyCellProps<AIAgentConfiguration>) =>
          getActionButton(row),
      },
    ],
    [getActionButton],
  );

  const filteredAgents = useMemo(
    () =>
      agents.filter((agent) => {
        // If the org name filter is undefined, we don't want to filter by org name.
        if (!orgNameFilter && !statusFilter) {
          return true;
        }

        const matchesOrgName =
          !orgNameFilter ||
          agent.org_name.toLowerCase().includes(orgNameFilter.toLowerCase());
        const matchesStatus = !statusFilter || agent.status === statusFilter;
        return matchesOrgName && matchesStatus;
      }),
    [agents, orgNameFilter, statusFilter],
  );

  return (
    <U21Section title="AI Agents">
      <U21Spacer spacing={3}>
        <U21Spacer horizontal>
          <StyledU21TextField
            placeholder="Filter by organization name..."
            value={orgNameFilter}
            onChange={(value: string) => setOrgNameFilter(value)}
          />
          <StyledU21Select
            value={statusFilter}
            onChange={(value) => setStatusFilter(value)}
            options={STATUS_OPTIONS}
            placeholder="All"
          />
        </U21Spacer>
        <U21Table
          columns={columns}
          data={filteredAgents}
          loading={isLoading}
          onRefresh={() => refetch()}
          refreshLoading={isRefetching}
        />
      </U21Spacer>
    </U21Section>
  );
};

const StyledU21TextField = styled(U21TextField)`
  width: 300px;
`;

const StyledU21Select = styled(U21Select<AIAgentConfiguration['status']>)`
  width: 200px;
`;
